<script setup lang="ts">
import BaseButton from "@/components/Base/Button.vue";
import type { BootstrapIconName } from "@/types/bootstrap-icons";
import type {
  ButtonKind,
  ButtonSize,
  ButtonTertiaryColor,
} from "@/types/components/Button.types";
import { Dropdown } from "bootstrap";
import type { Component } from "vue";

defineOptions({ name: "BaseDropdown" });

export type AutoCloseOptions = boolean | "inside" | "outside";

export type Props = {
  id: string;
  buttonClass?: string;
  buttonAriaLabel: string;
  buttonAttrs?: Record<string, unknown>;
  dropdownMenuClass?: string;
  inverted?: boolean;
  disabled?: boolean;
  autoClose?: AutoCloseOptions;
  dropup?: boolean;
  kind?: ButtonKind;
  tertiaryColor?: ButtonTertiaryColor;
  size?: ButtonSize;
  noPadding?: boolean;
  iconName?: BootstrapIconName;
  iconSvg?: Component;
  iconOnly?: boolean;
  iconOnlyMobile?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  dropdownMenuClass: "dropdown-menu-end",
  autoClose: true,
  kind: "tertiary",
  tertiaryColor: "main",
});

const emit = defineEmits(["show", "hide"]);

const dropdownRef = ref<HTMLElement | null>(null);
let dropdown: Dropdown | null = null;

onMounted(() => {
  if (dropdownRef.value) {
    const options = {
      autoClose: props.autoClose,
    };
    dropdown = new Dropdown(dropdownRef.value, options);
    dropdownRef.value.addEventListener("show.bs.dropdown", function () {
      emit("show");
    });
    dropdownRef.value.addEventListener("hide.bs.dropdown", function () {
      emit("hide");
    });
  }
});

onBeforeUnmount(() => {
  if (dropdown) {
    dropdown.dispose();
  }
});
</script>

<template>
  <div ref="dropdownRef" :class="dropup ? 'dropup' : 'dropdown'">
    <BaseButton
      :id="`dropdown-button-${id}`"
      :class="buttonClass"
      :disabled="disabled"
      :kind="kind"
      :tertiary-color="tertiaryColor"
      :size="size"
      :aria-label="buttonAriaLabel"
      :no-padding="noPadding"
      :icon-name="iconName"
      :icon-svg="iconSvg"
      :icon-only="iconOnly"
      :icon-only-mobile="iconOnlyMobile"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      v-bind="buttonAttrs"
    >
      <slot name="button"></slot>
    </BaseButton>
    <ul
      class="dropdown-menu"
      :class="dropdownMenuClass"
      :aria-labelledby="`dropdown-button-${id}`"
    >
      <slot name="default"></slot>
    </ul>
  </div>
</template>
